import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AktivitetType } from 'src/app/api/models/aktivitet-type';
import { ActionQueue } from 'src/app/core/components/action-queue/action-queue';
import { ErrorService } from 'src/app/core/error-handling/error.service';
import { FormHelper } from 'src/app/core/helpers/form-helper';
import { Rettighed } from 'src/app/core/models/rettighed';
import { NotificationService } from 'src/app/core/notifications/notification.service';
import { TekstResourcePipe } from 'src/app/core/pipes/tekstresource.pipe';
import { GenberegnOperation } from 'src/app/core/services/operations/genberegn/genberegn-operation';
import { SaveOperation } from 'src/app/core/services/operations/save/save-operation';
import { SaveOperationsService } from 'src/app/core/services/operations/save/save-operations.service';
import {
  AktivitetTypeIdentifikation,
  AktivitetTypeReference,
  AktivitetTypeWithVejledning,
  Dokumentation,
  EjendomSted,
  ForaeldetAktivitetTypePar,
  MatrikelSted,
  ProjektSted,
  PublicProjekt,
  PublicProjektDokumentation,
  PublicProjektRettighed,
  PublicProjektRettigheder,
  PublicProjektSag,
  PublicProjektSagKorrespondance,
  Sted,
  StedOplysninger
} from '../../../api/models';
import {
  LokaliteterService,
  ProjekterAktiviteterService,
  ProjekterService
} from '../../../api/services';
import { Direction } from '../../enums/direction-enum';

@Injectable({
  providedIn: 'root',
})
export class CurrentProjektService {
  projekt$ = new BehaviorSubject<PublicProjekt>(null);
  sted$ = new BehaviorSubject<ProjektSted>(null);
  aktiviteter$ = new BehaviorSubject<AktivitetType[]>([]);
  aktiviteterLoading$: Observable<AktivitetTypeIdentifikation[]>;
  beskeder$: Observable<PublicProjektSagKorrespondance[]>;
  laesteBeskeder$ = new BehaviorSubject<PublicProjektSagKorrespondance[]>([]);
  ulaesteBeskeder$ = new BehaviorSubject<number>(0);
  stedOplysninger$ = new BehaviorSubject<StedOplysninger>(undefined);
  rettigheder$ = new BehaviorSubject<PublicProjektRettigheder>(null);
  projektStedSub: Subscription;
  reset$ = new BehaviorSubject<boolean>(false);
  isMergeModalOpen$ = new BehaviorSubject<boolean>(false);
  mergeSag$ = new BehaviorSubject<PublicProjektSag>(null);
  mergeAktivitet$ = new BehaviorSubject<AktivitetTypeWithVejledning>(null);
  currentuserRettighed$ = new BehaviorSubject<PublicProjektRettighed>(null);
  aktivitetTypeAdded = new EventEmitter<AktivitetTypeIdentifikation>();
  aktivitetTypeChanged = new EventEmitter<AktivitetTypeIdentifikation>();
  queue = new ActionQueue();
  creatingProjekt$ = new BehaviorSubject<boolean>(false);
  isReadonly$: Observable<boolean>;

  private _aktiviteterLoading$ = new BehaviorSubject<AktivitetTypeIdentifikation[]>([]);
  inviteEjendomsejer: boolean;
  inviteMessage: string;

  constructor(
    private projekterService: ProjekterService,
    private projektAktiviteterService: ProjekterAktiviteterService,
    private saveOperationsService: SaveOperationsService,
    private router: Router,
    private lokaliteterService: LokaliteterService,
    private tekstResourcePipe: TekstResourcePipe,
    private notificationService: NotificationService,
    private errorService: ErrorService
  ) {
    this.aktiviteterLoading$ = this._aktiviteterLoading$.pipe(shareReplay());

    this.beskeder$ = combineLatest([
      this.projekt$.pipe(
        filter(projekt => !!projekt),
        filter(projekt => !!projekt.beskeder),
        map(projekt => projekt.beskeder)
      ),
      this.laesteBeskeder$,
    ]).pipe(
      map(values => {
        const projektBeskeder = values[0];
        const laesteBeskeder = values[1];
        for (const besked of projektBeskeder) {
          if (laesteBeskeder.indexOf(besked) >= 0) {
            besked.ulaest = false;
          }
        }
        return projektBeskeder;
      })
    );

    this.beskeder$
      .pipe(map(beskeder => beskeder.filter(besked => besked.ulaest).length))
      .subscribe(count => this.ulaesteBeskeder$.next(count));

    this.isReadonly$ = this.projekt$.pipe(
      filter(p => !!p),
      map(projekt => !!projekt.deletionDate)
    );
  }

  setSted(sted: ProjektSted) {
    this.inviteMessage = undefined;
    this.inviteEjendomsejer = undefined;
    this.projekt$.next({
      ...this.projekt$.value,
      ejerskabId: null,
      ejerskabIkkeSat: null,
      ejerskabIkkeSatBegrundelse: null,
      kontaktCvr: null,
      kontaktNavn: null,
    });
    this.sted$.next(sted);
  }

  setKontaktOgInvite(
    kontaktNavn: string,
    kontaktAdresse: string,
    kontaktEmail: string,
    kontaktTlf: string,
    kontaktCvr: string,
    inviteEjendomsejer: boolean,
    inviteMessage: string
  ) {
    this.inviteEjendomsejer = inviteEjendomsejer;
    this.inviteMessage = inviteMessage;
    this.setKontakt(kontaktNavn, kontaktAdresse, kontaktEmail, kontaktTlf, kontaktCvr);
  }

  setKontakt(
    kontaktNavn: string,
    kontaktAdresse: string,
    kontaktEmail: string,
    kontaktTlf: string,
    kontaktCvr: string
  ) {
    this.projekt$.next({
      ...this.projekt$.value,
      kontaktNavn: kontaktNavn,
      kontaktAdresse: kontaktAdresse,
      kontaktEmail: kontaktEmail,
      kontaktTlf: kontaktTlf,
      kontaktCvr: kontaktCvr,
    });
  }

  load(projektId: string) {
    this.projekt$.next({
      id: null,
      titel: 'Henter projekt...',
      sager: [],
      beskeder: [],
    } as PublicProjekt);

    this.queue.enqueue(this.projekterService.get(projektId)).subscribe({
      next: projekt => {
        this.projekterService.getProjektRettighed(projektId).subscribe(rettighed => {
          // Genberegn projekt in background and refresh it
          this.queue
            .enqueue(this.projekterService.genberegn(projekt.id))
            .subscribe(genberegnetProjekt => {
              this.projekt$.next(genberegnetProjekt);
              this.currentuserRettighed$.next(rettighed);

              // Notify pending aktiviteter, since these may exist after a redirect
              for (const aktivitetType of this._aktiviteterLoading$.value) {
                this.aktivitetTypeAdded.emit(aktivitetType);
              }
              this._aktiviteterLoading$.next([]);
              this.creatingProjekt$.next(false);
            });
        });

        this.projekterService.getProjektSted(projektId).subscribe(sted => {
          this.sted$.next(sted);
        });

        this.projektAktiviteterService
          .getAktivitetTyperForProjekt(projektId)
          .subscribe(aktivitetTyper => {
            this.aktiviteter$.next(aktivitetTyper);
          });

        this.projektStedSub = this.setUpStedSubscription();

        this.refreshRettigheder(projektId);
      },
      error: (err: HttpErrorResponse) => {
        this.errorService.handlePageLoadError(err);
      },
    });
  }

  refreshProjekt(
    saveOperation: SaveOperation = null,
    aktivitetType: AktivitetTypeIdentifikation = null,
    isAktivitetTypeChanged: boolean = false,
    genberegnOperation: GenberegnOperation = null
  ): Observable<PublicProjekt> {
    if (!this.projekt$.value || !this.projekt$.value.id) {
      return;
    }

    const getProjekt$ = this.queue.enqueue(this.projekterService.get(this.projekt$.value.id));

    getProjekt$.subscribe(x => {
      this.projekt$.next(x);

      if (saveOperation) {
        saveOperation.complete();
      }

      if (aktivitetType) {
        this.removeLoadingAktivitet(aktivitetType.aktivitetTypeReference);

        if (isAktivitetTypeChanged) {
          this.aktivitetTypeChanged.emit(aktivitetType);
        } else {
          this.aktivitetTypeAdded.emit(aktivitetType);
        }
      }

      if (genberegnOperation) {
        genberegnOperation.complete();
      }
    });

    return getProjekt$;
  }

  opdaterSag(sagId: string, genberegnOperation: GenberegnOperation = null): void {
    // For now, we just refresh projekt..
    // maybe in future we want to differentiate this method from refreshProjekt?
    this.refreshProjekt(genberegnOperation);
  }

  begin(genbrugFromProjektId: string = null) {
    this.reset();
    this.projekt$.next({
      id: undefined,
      titel: undefined,
      sager: [],
      beskeder: [],
      genbrugFromProjektId: genbrugFromProjektId,
    } as PublicProjekt);
    this.projektStedSub = this.setUpStedSubscription();
    this.currentuserRettighed$.next({ rettighed: 'ProjektEjer' });
  }

  reset() {
    this.projekt$.next(null);
    this.sted$.next(null);
    this.aktiviteter$.next([]);
    this._aktiviteterLoading$.next([]);
    this.laesteBeskeder$.next([]);
    this.ulaesteBeskeder$.next(0);
    this.stedOplysninger$.next(undefined);
    this.rettigheder$.next(null);
    this.currentuserRettighed$.next(null);
    this.reset$.next(true);
  }

  omdoebProjektTitel(projektTitel: string) {
    const saveOperation = this.saveOperationsService.addOperation();
    return this.projekterService
      .omdoeb({
        projektId: this.projekt$.value.id,
        projektTitel: projektTitel,
      })
      .pipe(
        tap(() => saveOperation.complete()),
        catchError(e => {
          saveOperation.fail();
          return throwError(e);
        })
      );
  }

  setEjendomsejer(ejerskabId: string) {
    const saveOperation = this.saveOperationsService.addOperation();
    return this.projekterService
      .setEjendomsejer({
        projektId: this.projekt$.value.id,
        ejerskabId: ejerskabId,
      })
      .pipe(
        tap(() => saveOperation.complete()),
        catchError(e => {
          saveOperation.fail();
          return throwError(e);
        })
      );
  }

  setNoEjendomsejer(ejerskabIkkeSatBegrundelse: string) {
    const saveOperation = this.saveOperationsService.addOperation();
    return this.projekterService
      .setNoEjendomsejer({
        projektId: this.projekt$.value.id,
        ejerskabIkkeSatBegrundelse: ejerskabIkkeSatBegrundelse,
      })
      .pipe(
        tap(() => saveOperation.complete()),
        catchError(e => {
          saveOperation.fail();
          return throwError(e);
        })
      );
  }

  isProjektKontaktInformationValid(): Observable<boolean> {
    return this.projekt$.pipe(map(projekt => this.validateProjektKontaktInformation(projekt)));
  }

  validateProjektKontaktInformation(projekt: PublicProjekt): boolean {
    if (!projekt.kontaktNavn) {
      return false;
    }
    if (!projekt.kontaktAdresse) {
      return false;
    }
    if (!projekt.kontaktTlf || !FormHelper.isDKTelefonValid(projekt.kontaktTlf)) {
      return false;
    }
    if (!projekt.kontaktEmail || !FormHelper.isEmailValid(projekt.kontaktEmail)) {
      return false;
    }
    if (!!projekt.kontaktCvr && !FormHelper.isCvrValid(projekt.kontaktCvr)) {
      return false;
    }
    if (this.inviteEjendomsejer && !this.inviteMessage) {
      return false;
    }
    return true;
  }

  setProjektKontaktInformation(
    kontaktNavn: string,
    kontaktAdresse: string,
    kontaktEmail: string,
    kontaktTlf: string,
    kontaktCvr: string
  ) {
    const saveOperation = this.saveOperationsService.addOperation();
    return this.projekterService
      .setProjektKontaktInformation({
        projektId: this.projekt$.value.id,
        kontaktNavn: kontaktNavn,
        kontaktAdresse: kontaktAdresse,
        kontaktEmail: kontaktEmail,
        kontaktTlf: kontaktTlf,
        kontaktCvr: kontaktCvr,
      })
      .pipe(
        tap(() => {
          this.setKontakt(kontaktNavn, kontaktAdresse, kontaktEmail, kontaktTlf, kontaktCvr);
          saveOperation.complete();
        }),
        catchError(e => {
          saveOperation.fail();
          return throwError(e);
        })
      );
  }

  addAktivitetType(aktivitetType: AktivitetTypeIdentifikation): Observable<string> {
    this.addLoadingAktivitet(aktivitetType);

    if (!this.projekt$.value.id) {
      return this.saveProjektWithInitialAktivitetType(
        this.sted$.value,
        aktivitetType.aktivitetTypeReference
      );
    } else {
      const saveOperation = this.saveOperationsService.addOperation();
      const sag = this.projekt$.value.sager.find(
        s => s.sagType.kode === aktivitetType.sagTypeKode && !s.erAfsluttet
      );
      if (sag) {
        this.mergeSag$.next(sag);
        this.mergeAktivitet$.next(aktivitetType);
        this.isMergeModalOpen$.next(true);
      }

      return this.saveAktivitetType(
        this.projekt$.value.id,
        aktivitetType.aktivitetTypeReference
      ).pipe(
        tap(() => {
          this.refreshProjekt(saveOperation, aktivitetType);
        }),
        catchError(e => {
          saveOperation.fail();
          return throwError(e);
        })
      );
    }
  }

  changeAktivitetType(
    aktivitetType: AktivitetTypeIdentifikation,
    sagToReplace: PublicProjektSag
  ): Observable<string> {
    this.addLoadingAktivitet(aktivitetType);

    const saveOperation = this.saveOperationsService.addOperation();
    const sag = this.projekt$.value.sager.find(
      s => s.sagType.kode === aktivitetType.sagTypeKode && !s.erAfsluttet
    );
    if (sag) {
      this.mergeSag$.next(sag);
      this.mergeAktivitet$.next(aktivitetType);

      // Dont show merge modal if sag is going to be replaced, as it wont merge due to sag being deleted
      if (!sag.genbrugFromSagId) {
        this.isMergeModalOpen$.next(true);
      }
    }

    // Update aktiviteter
    const aktivitetTypeIdsToBeRemoved = sagToReplace.aktiviteter.map(x => x.aktivitetType.id);
    const aktiviteter = this.aktiviteter$.value.filter(
      aktivitet => !aktivitetTypeIdsToBeRemoved.includes(aktivitet.id)
    );

    this.aktiviteter$.next(aktiviteter);

    return this.saveAktivitetType(
      this.projekt$.value.id,
      aktivitetType.aktivitetTypeReference,
      sagToReplace.id
    ).pipe(
      tap(() => {
        this.refreshProjekt(saveOperation, aktivitetType, true);
      }),
      catchError(e => {
        saveOperation.fail();
        return throwError(e);
      })
    );
  }

  updateDokumentation(dokumentation: Dokumentation) {
    const projekt = this.projekt$.value;
    if (!projekt) {
      return;
    }
    // Loop through all current projekt dokumentationer and update the one that match - if any
    let changed = false;
    for (const sag of projekt.sager) {
      for (const fase of sag.sagFaser) {
        const index = fase.dokumentationer.findIndex(d => d.id === dokumentation.id);

        if (index > -1) {
          // The current projekt service representation of a dokumentation is different from (but almost the same as)
          // the dokumentation representation we receive. Therefore we "cast" it and reflect the property name changes.
          const publicProjektDokumentation = dokumentation as PublicProjektDokumentation;
          publicProjektDokumentation.titel = dokumentation.visningNavn;

          fase.dokumentationer[index] = Object.assign(
            {},
            fase.dokumentationer[index],
            publicProjektDokumentation
          );

          changed = true;
          break;
        }
      }

      if (changed) {
        break;
      }
    }

    // If a change was made we update the projekt
    if (changed) {
      this.projekt$.next(projekt);
    }
  }

  private saveProjektWithInitialAktivitetType(
    projektSted: ProjektSted,
    aktivitetTypeReference: AktivitetTypeReference
  ) {
    this.creatingProjekt$.next(true);
    const saveOperation = this.saveOperationsService.addOperation();
    return this.projekterService
      .create({
        projektSted: projektSted,
        projektTitel: this.projekt$.value.titel,
        genbrugFromProjektId: this.projekt$.value.genbrugFromProjektId,
        ejerskabIkkeSat: this.projekt$.value.ejerskabIkkeSat,
        ejerskabId: this.projekt$.value.ejerskabId,
        ejerskabIkkeSatBegrundelse: this.projekt$.value.ejerskabIkkeSatBegrundelse,
        kontaktNavn: this.projekt$.value.kontaktNavn,
        kontaktAdresse: this.projekt$.value.kontaktAdresse,
        kontaktEmail: this.projekt$.value.kontaktEmail,
        kontaktTlf: this.projekt$.value.kontaktTlf,
        kontaktCvr: this.projekt$.value.kontaktCvr,
      })
      .pipe(
        switchMap(projekt =>
          this.saveAktivitetType(projekt.projektVersionReference.projektId, aktivitetTypeReference)
        ),
        tap(projektId => {
          this.router.navigate(['/projekter', 'nyt', projektId, 'aktiviteter']);
          saveOperation.complete();
        }),
        catchError(e => {
          saveOperation.fail();
          return throwError(e);
        })
      );
  }

  saveProjektWithEjendomsejerInvite() {
    const projekt = this.projekt$.value;
    if (this.inviteEjendomsejer && !projekt.id && !!this.inviteMessage) {
      const targetRoute = this.router.url.split('/').pop();
      if (targetRoute === 'aktiviteter' || targetRoute === 'tilknyttede-personer') {
        this.inviteEjendomsejer = undefined;
        return this.saveProjektWithInitialInvite(
          'Laes',
          projekt.kontaktNavn,
          this.inviteMessage,
          projekt.kontaktEmail,
          targetRoute
        )
          .pipe(
            catchError(e => {
              return throwError(e);
            })
          )
          .subscribe(() => {
            this.notificationService.success(this.tekstResourcePipe.transform('Invitation.Sendt'));
            this.refreshRettigheder();
          });
      }
    }
  }

  saveProjektWithInitialInvite(
    selectedPermission: 'ProjektEjer' | 'Ansoeg' | 'Dokumentation' | 'Laes',
    nameInput: string,
    messageInput: string,
    mailInput: string,
    targetRoute: string = 'tilknyttede-personer'
  ) {
    this.creatingProjekt$.next(true);
    const saveOperation = this.saveOperationsService.addOperation();
    let createdProjektId: string;
    return this.projekterService
      .create({
        projektSted: this.sted$.value,
        projektTitel: this.projekt$.value.titel,
        genbrugFromProjektId: this.projekt$.value.genbrugFromProjektId,
        ejerskabIkkeSat: this.projekt$.value.ejerskabIkkeSat,
        ejerskabId: this.projekt$.value.ejerskabId,
        ejerskabIkkeSatBegrundelse: this.projekt$.value.ejerskabIkkeSatBegrundelse,
        kontaktNavn: this.projekt$.value.kontaktNavn,
        kontaktAdresse: this.projekt$.value.kontaktAdresse,
        kontaktEmail: this.projekt$.value.kontaktEmail,
        kontaktTlf: this.projekt$.value.kontaktTlf,
        kontaktCvr: this.projekt$.value.kontaktCvr,
      })
      .pipe(
        map(projekt => projekt.projektVersionReference.projektId),
        switchMap(projektId => {
          createdProjektId = projektId;
          const inviterParams = {
            modtagerEmail: mailInput,
            modtagerMessage: messageInput,
            modtagerNavn: nameInput,
            modtagerRettighed: selectedPermission,
            projektId: projektId,
          } as ProjekterService.InviterParams;
          return this.projekterService.inviter(inviterParams);
        }),
        tap(() => {
          this.router.navigate(['/projekter', 'nyt', createdProjektId, targetRoute]);
          saveOperation.complete();
        }),
        catchError(e => {
          saveOperation.fail();
          return throwError(e);
        })
      );
  }

  saveProjektWithGenbrugFromProjekt() {
    this.creatingProjekt$.next(true);
    const saveOperation = this.saveOperationsService.addOperation();
    return this.projekterService
      .create({
        projektSted: this.sted$.value,
        projektTitel: this.projekt$.value.titel,
        genbrugFromProjektId: this.projekt$.value.genbrugFromProjektId,
        ejerskabIkkeSat: this.projekt$.value.ejerskabIkkeSat,
        ejerskabId: this.projekt$.value.ejerskabId,
        ejerskabIkkeSatBegrundelse: this.projekt$.value.ejerskabIkkeSatBegrundelse,
        kontaktNavn: this.projekt$.value.kontaktNavn,
        kontaktAdresse: this.projekt$.value.kontaktAdresse,
        kontaktEmail: this.projekt$.value.kontaktEmail,
        kontaktTlf: this.projekt$.value.kontaktTlf,
        kontaktCvr: this.projekt$.value.kontaktCvr,
      })
      .pipe(
        tap(projekt => {
          saveOperation.complete();
          this.router.navigate([
            '/projekter',
            'nyt',
            projekt.projektVersionReference.projektId,
            'aktiviteter',
          ]);
        }),
        catchError(e => {
          saveOperation.fail();
          return throwError(e);
        })
      );
  }

  GetForaeldedeAktiviteterToCopy(): Observable<Array<ForaeldetAktivitetTypePar>> {
    return this.projekterService.getForaeldetAktiviteter({ id: this.projekt$.value.id });
  }

  private saveAktivitetType(
    projektId: string,
    aktivitetTypeRef: AktivitetTypeReference,
    sagToReplaceId?: string
  ): Observable<string> {
    const saveOperation = this.saveOperationsService.addOperation();
    return this.projektAktiviteterService
      .addAktivitetTypeToProjekt({
        projektId,
        aktivitetTypeRef,
        sagToReplaceId,
      })
      .pipe(
        tap(aktivitetType => this.addAktivitet(aktivitetType)),
        tap(() => saveOperation.complete()),
        map(() => projektId),
        catchError(e => {
          saveOperation.fail();
          this.removeLoadingAktivitet(aktivitetTypeRef);
          return throwError(e);
        })
      );
  }

  private addAktivitet(aktivitetType: AktivitetType) {
    const aktiviteter = this.aktiviteter$.value;
    aktiviteter.push(aktivitetType);
    this.aktiviteter$.next(aktiviteter);
  }

  private addLoadingAktivitet(aktivitetTypeReference: AktivitetTypeWithVejledning) {
    const aktiviteterLoading = this._aktiviteterLoading$.value;
    aktiviteterLoading.push(aktivitetTypeReference);
    this._aktiviteterLoading$.next(aktiviteterLoading);
  }

  private removeLoadingAktivitet(aktivitetTypeReference: AktivitetTypeReference) {
    const aktiviteterLoading = this._aktiviteterLoading$.value.filter(
      x => x.aktivitetTypeReference.id !== aktivitetTypeReference.id
    );
    this._aktiviteterLoading$.next(aktiviteterLoading);
  }

  removeAktivitet(aktivitetTypeRef: AktivitetTypeReference): Observable<any> {
    const saveOperation = this.saveOperationsService.addOperation();
    const aktiviteter = this.aktiviteter$.value.filter(x => x.id !== aktivitetTypeRef.id);
    this.aktiviteter$.next(aktiviteter);

    const sagId = this.projekt$.value.sager.find(
      sag => !!sag.aktiviteter.find(akt => akt.aktivitetType.id === aktivitetTypeRef.id)
    ).id;

    return this.projektAktiviteterService
      .removeAktivitetTypeFromProjekt({
        projektId: this.projekt$.value.id,
        aktivitetTypeId: aktivitetTypeRef.id,
        regelkonfigurationId: aktivitetTypeRef.regelKonfigurationId,
        sagId: sagId,
      })
      .pipe(
        switchMap(() => this.refreshProjekt(saveOperation)),
        catchError(e => {
          saveOperation.fail();
          return throwError(e);
        })
      );
  }

  removeSag(sag: PublicProjektSag): Observable<void | PublicProjekt> {
    const saveOperation = this.saveOperationsService.addOperation();
    const sagAktivitetTyper = sag.aktiviteter.map(ppa => ppa.aktivitetType);
    const filteredAktiviteter = this.aktiviteter$.value.filter(
      x => !sagAktivitetTyper.find(ppat => ppat.id === x.id)
    );

    this.aktiviteter$.next(filteredAktiviteter);
    const aktivitetTypeReferencer = sagAktivitetTyper.map(ppat => {
      return {
        id: ppat.id,
        regelKonfigurationId: ppat.regelKonfigurationId,
      } as AktivitetTypeReference;
    });

    return this.projektAktiviteterService
      .removeAktivitetTyperFromProjekt({
        projektId: this.projekt$.value.id,
        aktivitetTypeReferencer: aktivitetTypeReferencer,
      })
      .pipe(
        switchMap(() => this.refreshProjekt()),
        tap(() => saveOperation.complete()),
        catchError(e => {
          saveOperation.fail();
          return throwError(e);
        })
      );
  }

  fetchBeskeder() {
    if (this.projekt$.value != null && this.projekt$.value.id != null) {
      this.projekterService
        .getBeskeder(this.projekt$.value.id)
        .pipe(take(1))
        .subscribe(beskeder => {
          this.projekt$.next({ ...this.projekt$.value, beskeder: beskeder });
        });
    }
  }

  setUpStedSubscription() {
    if (this.projektStedSub) {
      this.projektStedSub.unsubscribe();
    }
    return this.sted$.subscribe(projektSted => {
      if (projektSted == null) {
        if (this.stedOplysninger$.value !== undefined) {
          this.stedOplysninger$.next(undefined);
        }
        return;
      }

      const newStederArr: Sted[] = [];
      if (projektSted.steder && projektSted.steder.length > 0) {
        projektSted.steder.forEach(sted => {
          const ejendomSted = sted as EjendomSted;
          let isStedAdded = false;
          if (ejendomSted.matrikler && ejendomSted.matrikler.length === 0) {
            if (ejendomSted.adgangAdresse && ejendomSted.adgangAdresse) {
              const matrikelSted: MatrikelSted = ejendomSted.adgangAdresse.matrikel;
              ejendomSted.matrikler.push(matrikelSted);
              newStederArr.push(ejendomSted);
              isStedAdded = true;
            }
          }
          if (!isStedAdded) {
            newStederArr.push(sted);
          }
        });

        if (newStederArr.length > 0) {
          projektSted.steder = newStederArr;
        }
      }

      this.lokaliteterService.getStedOplysninger(projektSted).subscribe(stedOplysninger => {
        this.stedOplysninger$.next(stedOplysninger);
      });
    });
  }

  removeInvitation(invitationId: string): void {
    this.projekterService
      .tilbagekaldInvitation(invitationId)
      .subscribe(() => this.refreshRettigheder(this.projekt$.value.id));
  }

  refreshRettigheder(projektId?: string): void {
    if (!projektId && this.projekt$.value) {
      projektId = this.projekt$.value.id;
    }

    if (projektId) {
      this.projekterService.getProjektRettigheder(projektId).subscribe(rettigheder => {
        this.rettigheder$.next(rettigheder);
      });
    }
  }

  hasPermission(requiredRettighed: Rettighed, direction: Direction = Direction.AtLeast): boolean {
    if (
      !this.currentuserRettighed$.value ||
      requiredRettighed === undefined ||
      direction === undefined
    ) {
      return false;
    }
    switch (direction) {
      case Direction.AtLeast:
        return requiredRettighed.valueOf() >= this.currentuserRettighed$.value.rettighedAsValue;
      case Direction.AtMost:
        return requiredRettighed.valueOf() <= this.currentuserRettighed$.value.rettighedAsValue;
      case Direction.Exact:
        return requiredRettighed.valueOf() === this.currentuserRettighed$.value.rettighedAsValue;
    }
  }

  notiftyAktivitetTypeAdded(aktivitetType: AktivitetTypeIdentifikation) {
    this.notificationService.success(
      this.tekstResourcePipe.transform('Ansoegning.Tilfoejet.Description', {
        title: aktivitetType.navn,
      }),
      this.tekstResourcePipe.transform('Ansoegning.Tilfoejet.Title')
    );
  }

  notiftyAktivitetTypeChanged(aktivitetType: AktivitetTypeIdentifikation) {
    this.notificationService.success(
      this.tekstResourcePipe.transform('Ansoegning.AnsoegningstypeSkiftet.Description', {
        title: aktivitetType.navn,
      }),
      this.tekstResourcePipe.transform('Ansoegning.AnsoegningstypeSkiftet.Title')
    );
  }
}
